<template>
  <div class="plan-tile" :style="tileStyle" @click="onClick">
    <div class="plan-tile-bar" :style="barStyle"></div>

    <div v-if="isMaintenance" class="plan-tile-body">
      <div class="plan-tile-maint-code">{{ tile.WorkOrderCode }}</div>
      <div class="plan-tile-maint-name">{{ tile.ProductDescription1 }}</div>
    </div>

    <div v-else class="plan-tile-body">
      <div class="plan-tile-product">
        <span class="plan-tile-product-text">
          {{ tile.ProductCode }} - {{ tile.ProductDescription1 }}
        </span>
        <span v-if="isMTS" class="plan-mts-badge">MTS</span>
      </div>
      <div class="plan-tile-wo">{{ woCodeDisplay }}</div>
      <div v-if="isSO" class="plan-tile-customer">{{ tile.CustomerName }}</div>
      <div class="plan-tile-foot">
        <span class="plan-tile-qty">{{ quantityTonnes }} t</span>
        <span v-if="isSO" class="plan-tile-so"
          >SO-{{ tile.SalesOrderCode }}</span
        >
      </div>
    </div>
  </div>
</template>

<script>
import { formatNumber } from "devextreme/localization";
import { sequenceToAlpha } from "@/_helpers/formats/format";

function hexToRgb(hex) {
  const clean = (hex || "").replace("#", "");
  const bigint = parseInt(clean, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}

// Mixes a hex colour with white; ratio 0 = white, 1 = full colour
function tint(hex, ratio) {
  const { r, g, b } = hexToRgb(hex);
  const mix = (channel) => Math.round(channel * ratio + 255 * (1 - ratio));
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

export default {
  name: "PlanTile",

  props: {
    tile: { type: Object, required: true },
    // Count of SO lines linked to this tile's Work Order within this
    // process (Task 73070) — the alpha suffix only shows once 2+ lines
    // exist for the same WorkOrderCode+ProcessCode.
    lineCount: { type: Number, default: 1 },
  },

  emits: ["open-modal"],

  computed: {
    isMaintenance() {
      return this.tile.WOType === "Maintenance";
    },

    isMTS() {
      return !this.isMaintenance && this.tile.SalesOrderCode === 0;
    },

    isSO() {
      return !this.isMaintenance && !this.isMTS;
    },

    // Derived from Position (not WorkOrderSequence) — Position is written
    // directly per SO line on WorkOrderToSalesOrderLine, so it reliably
    // distinguishes lines within a Work Order.
    alphaSuffix() {
      return this.lineCount >= 2 ? sequenceToAlpha(this.tile.Position) : undefined;
    },

    woCodeDisplay() {
      return this.alphaSuffix
        ? `${this.tile.WorkOrderCode} ${this.alphaSuffix}`
        : this.tile.WorkOrderCode;
    },

    quantityTonnes() {
      // SalesOrderStockUMQuantity assumed to always be in KG; if a product's
      // Stock UM is not KG the displayed tonnes value will be incorrect (documented limitation)
      return formatNumber(
        (this.tile.SalesOrderStockUMQuantity || 0) / 1000,
        "##,###,###,###,##0.00",
      );
    },

    // Status/type -> colour mapping, matches the Timeline view's tile
    // palette (calendar-week-tile.vue / calendar-month-tile.vue /
    // calendar-day-line-tile.vue all share this computedClass logic) for
    // visual consistency across the Plan and Timeline views.
    accentColor() {
      if (this.isMaintenance) return "#808080";

      const status = this.tile.FriendlyStatus
        ? this.tile.FriendlyStatus.toFriendlyStatus()
        : false;

      switch (status) {
        case "Awaiting Production":
          return "#62d26f";
        case "In Production":
          return "#ffa500";
        case "Completed":
          return "#4186e0";
        case "Cancelled":
          return "#e8384f";
        case "Draft":
          return "#eec300";
        case "Planning":
          return "#8b5cf6";
        case "Review":
          return "#ff6f61";
        default:
          return "#9ca3af";
      }
    },

    tileStyle() {
      return {
        background: tint(this.accentColor, 0.1),
        borderColor: tint(this.accentColor, 0.4),
      };
    },

    barStyle() {
      return { background: this.accentColor };
    },
  },

  methods: {
    onClick() {
      this.$emit("open-modal", {
        WOType: this.tile.WOType,
        WorkOrderCode: this.tile.WorkOrderCode,
        RecordId: this.tile.RecordId,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.plan-tile {
  border-radius: 2px;
  overflow: hidden;
  border: 1px solid transparent;
  cursor: pointer;
  position: relative;

  &:hover {
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.18);
  }

  .plan-tile-bar {
    height: 3px;
    width: 100%;
  }

  .plan-tile-body {
    padding: 5px 6px 6px;
  }

  .plan-tile-product {
    font-size: 11px;
    font-weight: 700;
    color: #111827;
    line-height: 1.3;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 4px;
  }

  .plan-tile-product-text {
    flex: 1;
    min-width: 0;
    white-space: normal;
    word-break: break-word;
  }

  .plan-mts-badge {
    display: inline-flex;
    align-items: center;
    height: 14px;
    font-size: 8px;
    font-weight: 700;
    color: #fff;
    background: #64748b;
    border-radius: 2px;
    padding: 0 4px;
    letter-spacing: 0.04em;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .plan-tile-wo {
    font-size: 10px;
    color: #64748b;
    margin-top: 2px;
    font-variant-numeric: tabular-nums;
  }

  .plan-tile-customer {
    font-size: 11px;
    color: #374151;
    margin-top: 2px;
  }

  .plan-tile-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 4px;
    gap: 4px;
  }

  .plan-tile-qty {
    font-size: 11px;
    font-weight: 600;
    color: #111827;
    font-variant-numeric: tabular-nums;
  }

  .plan-tile-so {
    font-size: 10px;
    color: #9ca3af;
    font-variant-numeric: tabular-nums;
  }

  .plan-tile-maint-code {
    font-size: 11px;
    font-weight: 700;
    color: #111827;
  }

  .plan-tile-maint-name {
    font-size: 10px;
    color: #4b5563;
    margin-top: 2px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
}
</style>
